<template>
  <v-dialog-wrapper
    :value="openDialog"
    :max-width="maxWidth"
    :fullscreen="isMobile"
    :persistent="persistent"
    :transition="dialogTransition"
    @input="value => !value && closeDialog()"
    @keydown.esc="() => !persistent && closeDialog()"
    @keydown.enter="confirmDialog"
  >
    <v-card-wrapper
      class="v-card"
      :class="{ 'v-card--mobile': isMobile }"
      :style="cardStyle"
    >
      <loader-component
        v-if="showLoader"
        :show="showLoader"
      />

      <!--      TITLE-->
      <v-card-title-wrapper
        class="v-card-title"
        :style="{ borderBottom: subtitle ? 'unset' : '' }"
      >
        <span
          ref="titleRef"
          class="v-card-title__text"
          :class="{ 'v-card-title__text--truncate': truncateTitle }"
          :title="truncateTitle && isTitleTruncated ? title : undefined"
        >
          {{ title }}
        </span>

        <div
          class="x-image"
          :class="{ 'x-image--disabled': showLoader }"
        >
          <v-img
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @click="closeDialog"
          />
        </div>
      </v-card-title-wrapper>

      <!--      SUBTITLE-->
      <v-card-subtitle
        v-if="subtitle"
        class="v-card-subtitle"
      >
        {{ subtitle }}
      </v-card-subtitle>

      <v-card-text-wrapper
        class="dialog-message"
        style="overflow-y: auto; flex-grow: 1; padding-top: 20px;"
      >
        <slot />
      </v-card-text-wrapper>

      <v-card-actions-wrapper class="v-card-actions">
        <slot name="actions">
          <v-row-wrapper
            style="margin: 0;"
          >
            <v-col-wrapper>
              <universal-button
                type="cancel"
                :label="negativeButtonText"
                :disabled="showLoader"
                class="universal-buttons"
                @click="closeDialog"
              />
            </v-col-wrapper>
            <v-col-wrapper>
              <universal-button
                type="base"
                :label="positiveButtonText"
                :disabled="showLoader"
                class="universal-buttons"
                color="#65AD45"
                style="color: white !important;"
                @click="confirmDialog"
              />
            </v-col-wrapper>
          </v-row-wrapper>
        </slot>
      </v-card-actions-wrapper>
    </v-card-wrapper>
  </v-dialog-wrapper>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useAppContext } from '@/global/services/compositionApi/useAppContext'
import { isFunction } from '@/global/utils/utils'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import LoaderComponent from '@/global/components/loader/Loader.vue'
import VDialogWrapper from '@/global/components/vuetifyComponentsWrappers/VDialogWrapper.vue'
import VCardWrapper from '@/global/components/vuetifyComponentsWrappers/VCardWrapper.vue'
import VCardTitleWrapper from '@/global/components/vuetifyComponentsWrappers/VCardTitleWrapper.vue'
import VCardTextWrapper from '@/global/components/vuetifyComponentsWrappers/VCardTextWrapper.vue'
import VCardActionsWrapper from '@/global/components/vuetifyComponentsWrappers/VCardActionsWrapper.vue'
import VRowWrapper from '@/global/components/vuetifyComponentsWrappers/VRowWrapper.vue'
import VColWrapper from '@/global/components/vuetifyComponentsWrappers/VColWrapper.vue'

const props = defineProps({
  openDialog: {
    type: Boolean,
    default: false
  },
  positiveButtonText: {
    type: String,
    default: ''
  },
  negativeButtonText: {
    type: String,
    default: ''
  },
  confirmCallbackFunction: {
    type: Function,
    default: null
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  // Covers the card with a spinner and locks the actions - for the awaits a dialog
  // does on its own, such as reading the record it is about to edit, or saving it.
  showLoader: {
    type: Boolean,
    default: false
  },
  // Blocks outside-click/esc from closing the dialog on their own - used by
  // every existing consumer (confirm/cancel flows), so it defaults to true.
  // Consumers that need native outside-click/esc closing (eg. FilesPreview,
  // which has no destructive action to guard) opt out explicitly.
  persistent: {
    type: Boolean,
    default: true
  },
  maxWidth: {
    type: [String, Number],
    default: 674
  },
  // Vuetify's dialog transition, passed on explicitly: an undeclared `transition`
  // would fall through as a plain DOM attribute on the dialog container instead of
  // reaching `v-dialog-wrapper` as a prop, and so would never animate anything.
  // `false` - no animation - is what every dialog here has had, so it stays the
  // default; `true` asks for Vuetify's own dialog animation, and a string names a
  // transition of its own.
  transition: {
    type: [String, Boolean],
    default: false
  },
  // Keeps the title on a single line (ellipsis + hover tooltip when cut off)
  // instead of the default behaviour of wrapping onto multiple lines.
  truncateTitle: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'close',
  'close-inner'
])

const { vuetify } = useAppContext()

const titleRef = ref(null)
const isTitleTruncated = ref(false)

const isMobile = computed(() => !!vuetify?.breakpoint?.smAndDown)

// The card is held to `maxWidth` too, not only the dialog around it - otherwise a
// dialog asked to be wider than the default would open wider and leave its card at
// the default width inside it. A number is pixels, as it is to `v-dialog`.
const cardStyle = computed(() => {
  const { maxWidth } = props

  if (maxWidth === null || maxWidth === undefined || maxWidth === '') {
    return {}
  }

  return {
    '--index-dialog-max-width': typeof maxWidth === 'number' || /^\d+$/.test(maxWidth) ? `${maxWidth}px` : maxWidth
  }
})

// `v-dialog` wants a transition *name*, so a plain `true` is spelled out here rather
// than handed on as-is - passed through it would name a transition called "true",
// whose CSS does not exist, and the dialog would animate no more than with `false`.
const dialogTransition = computed(() => props.transition === true ? 'dialog-transition' : props.transition)

function checkTitleTruncation () {
  if (!props.truncateTitle) {
    return
  }

  nextTick(() => {
    const el = titleRef.value
    isTitleTruncated.value = !!el && el.scrollWidth > el.clientWidth
  })
}

watch(() => props.title, checkTitleTruncation)
watch(() => props.openDialog, (val) => val && checkTitleTruncation())
onMounted(checkTitleTruncation)

function closeDialog () {
  if (props.showLoader) {
    return
  }

  emit('close')
}

async function confirmDialog () {
  if (props.showLoader) {
    return
  }

  if (isFunction(props.confirmCallbackFunction)) {
    await props.confirmCallbackFunction()
  }
  emit('close-inner')
}
</script>

<style scoped lang="scss">
@import "@/global/assets/css/mixins/deep";

.dialog-message {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 20px !important;
  line-height: 32px;
  color: #111827 !important;
}

.v-card {
  max-width: var(--index-dialog-max-width, 674px);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  border-radius: 16px !important;
  overflow: hidden;
}

.universal-buttons {
  width: 100%;
  height: 40px !important;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 17px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
  letter-spacing: 0 !important;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: nowrap;
}

.v-card-title__text {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.v-card-title__text--truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-card-subtitle {
  border-bottom: 1px solid #E5E7EB;
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  letter-spacing: 0 !important;
  color: #6B7280 !important;
  padding: 10px 0 10px 24px !important;
}

.x-image:hover {
  cursor: pointer;
}

.x-image--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.v-card-actions {
  margin-top: auto;
}

@include deep('.v-dialog__content .v-dialog') {
  border-radius: 16px;
}

@include deep('.v-dialog__content') {
  z-index: 999 !important;
}

.v-card--mobile {
  max-width: 100%;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  border-radius: 0 !important;
}

@media (max-width: 960px) {
  .v-card-title {
    font-size: 15px !important;
    padding: 16px 16px 14px 16px;
    align-items: center;
  }

  .v-card-title__text {
    padding-right: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-card-subtitle {
    padding: 10px 16px !important;
  }

  .dialog-message {
    font-size: 16px !important;
    line-height: 24px;
    padding-left: 16px;
    padding-right: 16px;
  }

  .v-card-actions {
    padding: 16px;
  }

  .v-card-actions .row {
    width: 100%;
  }

  @include deep('.v-dialog__content .v-dialog') {
    border-radius: unset !important;
  }
}
</style>
